import { useMemo, useCallback } from 'react';
import { pricing as defaultPricing, getUserLabel } from '../data/pricing';

/**
 * Custom hook for quote calculations
 *
 * QuoteData structure:
 * - reportsEBS: [] (flat array of module names)
 * - reportsCloud: [] (flat array of module names)
 * - financialAnalytics: []
 * - peopleAnalytics: [] (PEPM)
 * - glConnectOnPremise: boolean
 * - glConnectCloud: boolean
 * - crm: []
 * - erpConnectors: []
 * - dataPipelines: []
 * - utilities: []
 */
const useQuoteCalculations = (quoteData, pricingParam) => {
  const pricing = pricingParam || defaultPricing;

  // Find item in domain-based reports section
  const findReportsItem = useCallback((section, name) => {
    const domains = Object.keys(pricing[section] || {});
    for (const domain of domains) {
      const items = pricing[section][domain] || [];
      const found = items.find(item => item.name === name);
      if (found) return found;
    }
    return null;
  }, [pricing]);

  // Find item in flat array section
  const findItem = useCallback((section, name) => {
    const items = pricing[section];
    if (!Array.isArray(items)) return null;
    return items.find(item => item.name === name);
  }, [pricing]);

  /**
   * Calculate all line items from the quote configuration
   */
  const calculateLineItems = useCallback(() => {
    const items = [];

    // Platform - handle prospect bundle vs existing customer
    const isProspect = quoteData.customerType === 'prospect';

    if (isProspect && quoteData.hasSplashBI && quoteData.hasSplashAI) {
      // Prospect: Combined SplashBIxAI Platform bundle
      const bundle = pricing.platform.prospectBundle || {};
      items.push({
        category: 'platform',
        sku: bundle.sku || pricing.platform.SplashAI?.sku,
        name: bundle.name || 'SplashBIxAI Platform',
        listPrice: bundle.price || 100000,
        quantity: 1
      });
    } else {
      // Existing customer: Separate platforms
      if (quoteData.hasSplashBI) {
        items.push({
          category: 'platform',
          sku: pricing.platform.SplashBI.sku,
          name: 'SplashBI Platform',
          listPrice: pricing.platform.SplashBI?.price || 60000,
          quantity: 1
        });
      }
      if (quoteData.hasSplashAI) {
        items.push({
          category: 'platform',
          sku: pricing.platform.SplashAI.sku,
          name: 'SplashAI Platform',
          listPrice: pricing.platform.SplashAI?.price || 60000,
          quantity: 1
        });
      }
    }

    // Deployment
    if (quoteData.deployment) {
      const deploy = pricing.deployment[quoteData.deployment];
      if (deploy) {
        items.push({
          category: 'platform',
          sku: deploy.sku,
          name: `Deployment: ${quoteData.deployment}`,
          listPrice: deploy.price,
          quantity: 1
        });
      }
    }

    // ERP Connectors
    if (quoteData.erpConnectors && Array.isArray(quoteData.erpConnectors)) {
      quoteData.erpConnectors.forEach(name => {
        const conn = findItem('erpConnectors', name);
        if (conn) {
          items.push({
            category: 'erpConnectors',
            sku: conn.sku,
            name: name,
            listPrice: conn.price,
            quantity: 1
          });
        }
      });
    }

    // Users
    if (quoteData.users) {
      Object.keys(quoteData.users).forEach(type => {
        if (quoteData.users[type] > 0 && pricing.users[type]) {
          const userInfo = pricing.users[type];
          items.push({
            category: 'users',
            sku: userInfo.sku,
            name: getUserLabel(type),
            listPrice: userInfo.price,
            quantity: quoteData.users[type]
          });
        }
      });
    }

    // Data Pipelines
    if (quoteData.dataPipelines && Array.isArray(quoteData.dataPipelines)) {
      quoteData.dataPipelines.forEach(name => {
        const pipe = findItem('dataPipelines', name);
        if (pipe) {
          items.push({
            category: 'dataPipelines',
            sku: pipe.sku,
            name: `Pipeline: ${name}`,
            listPrice: pipe.price,
            quantity: 1
          });
        }
      });
    }

    // Reports - EBS (flat array of names, lookup in domain structure)
    if (quoteData.reportsEBS && Array.isArray(quoteData.reportsEBS)) {
      quoteData.reportsEBS.forEach(name => {
        const mod = findReportsItem('reportsEBS', name);
        if (mod) {
          items.push({
            category: 'reportsEBS',
            sku: mod.sku,
            name: `EBS: ${name}`,
            listPrice: mod.price,
            quantity: 1
          });
        }
      });
    }

    // Reports - Cloud (flat array of names)
    if (quoteData.reportsCloud && Array.isArray(quoteData.reportsCloud)) {
      quoteData.reportsCloud.forEach(name => {
        const mod = findReportsItem('reportsCloud', name);
        if (mod) {
          items.push({
            category: 'reportsCloud',
            sku: mod.sku,
            name: `Cloud: ${name}`,
            listPrice: mod.price,
            quantity: 1
          });
        }
      });
    }

    // Financial Analytics
    if (quoteData.financialAnalytics && Array.isArray(quoteData.financialAnalytics)) {
      quoteData.financialAnalytics.forEach(name => {
        const mod = findItem('financialAnalytics', name);
        if (mod) {
          items.push({
            category: 'financialAnalytics',
            sku: mod.sku,
            name: `Financial Analytics: ${name}`,
            listPrice: mod.price,
            quantity: 1
          });
        }
      });
    }

    // People Analytics (PEPM) - price calculated from PEPM rate * employees * 12
    if (quoteData.peopleAnalytics && Array.isArray(quoteData.peopleAnalytics)) {
      const employeeCount = quoteData.employeeCount || 0;
      quoteData.peopleAnalytics.forEach(name => {
        const mod = findItem('peopleAnalytics', name);
        if (mod) {
          // Annual price = PEPM * employees * 12 months
          const annualPrice = (mod.pepm || 0) * employeeCount * 12;
          items.push({
            category: 'peopleAnalytics',
            sku: mod.sku,
            name: `People Analytics: ${name}`,
            listPrice: annualPrice,
            quantity: 1,
            isPEPM: true,
            pepmRate: mod.pepm
          });
        }
      });
    }

    // CRM
    if (quoteData.crm && Array.isArray(quoteData.crm)) {
      quoteData.crm.forEach(name => {
        const mod = findItem('crm', name);
        if (mod) {
          items.push({
            category: 'crm',
            sku: mod.sku,
            name: `CRM: ${name}`,
            listPrice: mod.price,
            quantity: 1
          });
        }
      });
    }

    // GL Connect
    if (quoteData.glConnectOnPremise && pricing.glConnect?.onPremise) {
      items.push({
        category: 'glConnect',
        sku: pricing.glConnect.onPremise.sku,
        name: 'GL Connect - On Premise (EBS)',
        listPrice: pricing.glConnect.onPremise.price,
        quantity: 1
      });
    }
    if (quoteData.glConnectCloud && pricing.glConnect?.cloud) {
      items.push({
        category: 'glConnect',
        sku: pricing.glConnect.cloud.sku,
        name: 'GL Connect - Cloud',
        listPrice: pricing.glConnect.cloud.price,
        quantity: 1
      });
    }

    // Utilities
    if (quoteData.utilities && Array.isArray(quoteData.utilities)) {
      quoteData.utilities.forEach(name => {
        const util = findItem('utilities', name);
        if (util) {
          items.push({
            category: 'utilities',
            sku: util.sku,
            name: `Utility: ${name}`,
            listPrice: util.price,
            quantity: 1
          });
        }
      });
    }

    // Services (one-time)
    if (quoteData.services && typeof quoteData.services === 'object') {
      Object.entries(quoteData.services).forEach(([service, quantity]) => {
        if (quantity > 0 && pricing.services[service]) {
          const svc = pricing.services[service];
          items.push({
            category: 'services',
            sku: svc.sku,
            name: service,
            listPrice: svc.price,
            quantity: quantity,
            isOneTime: true
          });
        }
      });
    }

    // Mark existing products (already owned by existing customers)
    const existingSkus = new Set(quoteData.existingProducts || []);
    items.forEach(item => {
      if (existingSkus.has(item.sku)) {
        item.isExisting = true;
        item.originalListPrice = item.listPrice;
        item.listPrice = 0;
      }
    });

    return items;
  }, [quoteData, findItem, findReportsItem, pricing]);

  const lineItems = useMemo(() => calculateLineItems(), [calculateLineItems]);

  const totals = useMemo(() => {
    let arrSubtotal = 0;
    let oneTimeSubtotal = 0;
    let arrDiscount = 0;
    let oneTimeDiscount = 0;

    lineItems.forEach(item => {
      const itemTotal = item.listPrice * item.quantity;
      const categoryDiscount = (quoteData.categoryDiscounts?.[item.category] || 0) / 100;
      const lineItemDiscount = (quoteData.lineItemDiscounts?.[item.sku] || 0) / 100;
      const effectiveDiscount = lineItemDiscount > 0 ? lineItemDiscount : categoryDiscount;
      const discountAmount = itemTotal * effectiveDiscount;

      if (item.isOneTime) {
        oneTimeSubtotal += itemTotal;
        oneTimeDiscount += discountAmount;
      } else {
        arrSubtotal += itemTotal;
        arrDiscount += discountAmount;
      }
    });

    return {
      arrSubtotal,
      oneTimeSubtotal,
      subtotal: arrSubtotal + oneTimeSubtotal,
      arrDiscount,
      oneTimeDiscount,
      totalDiscount: arrDiscount + oneTimeDiscount,
      totalARR: arrSubtotal - arrDiscount,
      totalOneTime: oneTimeSubtotal - oneTimeDiscount,
      total: (arrSubtotal - arrDiscount) + (oneTimeSubtotal - oneTimeDiscount)
    };
  }, [lineItems, quoteData.categoryDiscounts, quoteData.lineItemDiscounts]);

  const calculatePEPM = useCallback(() => {
    const { employeeCount, hcmPlatform, hcmPlatformPepm, dealType } = quoteData;

    // Only show PEPM for HCM or Cross-Domain deals with employee count
    const isHCMDeal = dealType === 'HCM' || dealType === 'Cross-Domain';
    if (!isHCMDeal || !employeeCount || employeeCount === 0) {
      return null;
    }

    // Calculate SplashBI PEPM from selected People Analytics modules
    let splashPepm = 0;
    (quoteData.peopleAnalytics || []).forEach(name => {
      const mod = (pricing.peopleAnalytics || []).find(m => m.name === name);
      if (mod?.pepm) {
        splashPepm += mod.pepm;
      }
    });

    // Get HCM platform benchmark PEPM
    let benchmarkPepm = hcmPlatformPepm || 0;
    if (!benchmarkPepm && hcmPlatform && pricing.hcmBenchmarks?.platforms[hcmPlatform]) {
      // Estimate based on employee count tier
      const tier = pricing.hcmBenchmarks.tiers.find(t => employeeCount <= t.maxEmployees);
      benchmarkPepm = tier?.typicalPepm || 17;
    }

    // Calculate 25% threshold
    const maxAllowedPepm = benchmarkPepm * (pricing.hcmBenchmarks?.maxSplashPercentage || 0.25);
    const isWithinThreshold = splashPepm <= maxAllowedPepm || !benchmarkPepm;
    const percentageOfHcm = benchmarkPepm > 0 ? (splashPepm / benchmarkPepm * 100) : 0;

    // Annual calculations
    const splashAnnual = splashPepm * employeeCount * 12;

    return {
      isHCMDeal: true,
      employeeCount,
      splashPepm: splashPepm.toFixed(2),
      splashPepmRaw: splashPepm,
      splashAnnual: Math.round(splashAnnual),
      hcmPlatform,
      hcmPlatformPepm: benchmarkPepm,
      maxAllowedPepm: maxAllowedPepm.toFixed(2),
      isWithinThreshold,
      percentageOfHcm: percentageOfHcm.toFixed(1),
      selectedModules: quoteData.peopleAnalytics?.length || 0,
      totalModules: pricing.peopleAnalytics?.length || 0
    };
  }, [quoteData, pricing]);

  const getEffectiveDiscount = useCallback((item) => {
    const lineDiscount = quoteData.lineItemDiscounts?.[item.sku] || 0;
    const catDiscount = quoteData.categoryDiscounts?.[item.category] || 0;
    return lineDiscount > 0 ? lineDiscount : catDiscount;
  }, [quoteData.lineItemDiscounts, quoteData.categoryDiscounts]);

  const calculateItemDiscount = useCallback((item) => {
    return (item.listPrice * item.quantity) * (getEffectiveDiscount(item) / 100);
  }, [getEffectiveDiscount]);

  const calculateItemTotal = useCallback((item) => {
    return (item.listPrice * item.quantity) - calculateItemDiscount(item);
  }, [calculateItemDiscount]);

  return {
    lineItems,
    totals,
    calculateLineItems,
    calculatePEPM,
    getEffectiveDiscount,
    calculateItemDiscount,
    calculateItemTotal,
    pricing
  };
};

export default useQuoteCalculations;
